/**
 * LaunchTab — catalog list + KPI tiles + tagline + help icon.
 *
 * Ported from v1 (mcp/src/outlook-addin/src/components/HomeDashboard.tsx).
 *
 * Restyle: hex → Highstreet Tailwind tokens; inline <style> keyframes
 * (livePulse / runningPulse / fadeUp) moved to tailwind.config.ts.
 *
 * Backend rewiring per plan §"Backend rewiring rules":
 *   v1 api.getActiveWorkflows(userId)  → orchestratorApi.listInstances(running+paused_hitl)
 *   v1 api.getMetrics()                → orchestratorApi.getMyKpis("7d"); its
 *     completed_by_workflow drives the catalog's usage sort (2026-10-06 — was
 *     a 200-row listInstances read counted client-side)
 *   v1 api.getManifest()               → App's ONE compact catalog read (catalogFromHost, 2026-10-06); orchestratorApi.getCatalog() only when no host passes it
 *   Polling cadence: 30s → 5s on visible tab, 30s on hidden
 *     (document.visibilityState).
 *
 * Adds a "Suggest workflow" button next to SelectedClientTile that fires
 * `analyze_email` async; on result we highlight the matching catalog
 * card with `ring-2 ring-sunrise motion-safe:animate-pulse` for 5s.
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { orchestratorApi, buildAnalyzeEmailPayload } from "../services/orchestratorApi";
import type {
  WorkflowCatalogEntry,
  WorkflowKpis,
} from "../types/workflow";
import type { EmailContext } from "../hooks/useMailboxItem";
import { useActiveInstances } from "../hooks/useActiveInstances";
import { ClientContextTile, type ClientContextTileProps } from "@product-app/common/workflows/components/ClientContextTile";
import type { ClientDetails } from "@product-app/common/workflows/types/client";
import ErrorPanel from "./ErrorPanel";
import Skeleton from "./Skeleton";
import Spinner from "./Spinner";
import Toast from "./Toast";
import type { UseClientContextValue } from "../hooks/useClientContext";
import { CatalogCard, workflowLabel } from "./CatalogCard";
import { ActiveWorkflowCard, statusDotBg, statusTone, statusLabel } from "./ActiveWorkflowCard";
import {
  isSharedMailboxLaunchGated,
  type UseSharedMailboxTargetResult,
} from "../features/sharedMailbox/sharedMailboxTarget";
import { workflowRequiresClient } from "../features/launchGuards";
import { isLaunchableInPlugin } from "../features/pluginCatalogExclusions";
const SUGGEST_RING_TTL_MS = 5_000;

// ─── LaunchTab ────────────────────────────────────────────────────────────────

export interface LaunchTabProps {
  /** Open a catalog entry (catalog browse path). Receives the full entry so
   *  the parent can pass input_schema + description down to the drawer. */
  onOpenCatalog: (entry: WorkflowCatalogEntry) => void;
  /** Open an in-flight workflow row. */
  onOpenWorkflow: (instanceId: string, workflowId: string, catalogEntry?: WorkflowCatalogEntry) => void;
  /** Instance id of the currently-open drawer, used to highlight the active In Flight row. */
  activeInstanceId?: string;
  /** Demo mode flag — drives banner copy + skips real polling. */
  demoMode?: boolean;
  /** Lifted client context (App.tsx owns it; LaunchTab + Drawer share). */
  clientCtx?: UseClientContextValue;
  /** Currently-selected email (drives "Suggest workflow" button visibility). */
  emailContext?: EmailContext | null;
  /** SignalR connected indicator (drives the "live" dot). */
  isLiveConnected?: boolean;
  /** Workflow ref to highlight, set by the auto-process path in App.tsx. */
  autoSuggestedWorkflowRef?: string | null;
  /** Called whenever the computed KPI metrics change (used by App.tsx overlay). */
  onMetricsUpdate?: (metrics: { total: number; hours_saved: number } | null) => void;
  /** Called when the user clears the client context — parent should clear the auto-suggested workflow ref. */
  onClearSuggestion?: () => void;
  /** Called when a manual "Suggest workflow" returns a match — parent should update the auto-suggested ref. */
  onSuggestion?: (ref: string) => void;
  /**
   * Called by the "Launch <workflow_title>" CTA. Parent should open the
   * InstanceDetailDrawer in pre_launch mode with the supplied prefill values.
   * Receives the full catalog entry so the parent can wire input_schema /
   * description into the drawer without a second catalog lookup.
   */
  onLaunchRecommended?: (entry: WorkflowCatalogEntry, prefill: Record<string, unknown>) => void;
  /** Called whenever the in-flight count changes — drives the "Active" tab badge. */
  onCountChange?: (count: number) => void;
  /**
   * Lifted `useSharedMailboxTarget()` result (App.tsx mounts the hook once —
   * plan Phase 3 task 1). Undefined/omitted behaves exactly like `status:
   * "off"` (no new DOM, no launch gating) — the legacy launch path is
   * unaffected when this prop isn't supplied.
   */
  sharedMailboxTarget?: UseSharedMailboxTargetResult;
  /**
   * The catalog App already loaded on pane open (plugin catalog round,
   * 2026-10-06). When present this view never reads its own: every Launch open —
   * this tab and the "Choose a workflow" takeover — used to re-download the
   * identical catalog. `loading` is App's first read still in flight (the
   * skeleton, not "no workflows"); `error` is a read that failed with no list to
   * show; `onRetry` re-reads App's copy. Omitted (tests, other hosts): this view
   * loads the catalog itself, as before.
   */
  catalogFromHost?: {
    entries: WorkflowCatalogEntry[];
    loading: boolean;
    error: unknown;
    onRetry: () => void;
  };
}

export const LaunchTab: React.FC<LaunchTabProps> = ({
  onOpenCatalog,
  onOpenWorkflow,
  demoMode,
  clientCtx,
  emailContext,
  isLiveConnected,
  autoSuggestedWorkflowRef,
  onMetricsUpdate,
  onClearSuggestion,
  onSuggestion,
  onLaunchRecommended,
  activeInstanceId,
  onCountChange,
  sharedMailboxTarget,
  catalogFromHost,
}) => {
  // ── Shared-mailbox launch gate ────────────────────────────────────────────
  // No "Acting on" confirmation and no picker — a shared launch is threaded
  // silently (identical to a personal launch). The ONLY gate is "pending": the
  // flag GET or the getSharedPropertiesAsync round-trip is still in flight, so
  // we don't yet know shared vs personal. Blocking that sub-second window stops
  // a shared launch from slipping through ungated as personal. "off"/"personal"
  // (or the prop absent) ⇒ zero gating, byte-identical to the legacy surface.
  const smStatus = sharedMailboxTarget?.status ?? "off";
  const sharedPending = isSharedMailboxLaunchGated(smStatus);
  // Client-required workflows (currently the mortgagee update) can't launch
  // until a client context is resolved (add-in only). isClientDone is derived
  // below from clientCtx; captured here for the per-card guard.
  const isClientDone = clientCtx?.state === "done";
  const gatedOpenCatalog = useCallback(
    (entry: WorkflowCatalogEntry) => {
      if (sharedPending) return; // shared/personal not resolved yet
      if (workflowRequiresClient(entry) && !isClientDone) return; // needs a client first
      onOpenCatalog(entry);
    },
    [sharedPending, isClientDone, onOpenCatalog],
  );
  // Active instances are owned by useActiveInstances — push-aware (subscribes
  // per row, drops fast-cadence polling once SignalR connects). Replaces the
  // local listInstances poll that previously fired every 5s regardless of
  // SignalR state.
  const { rows: activeWorkflows, loading: loadingActive } = useActiveInstances();
  const [kpis, setKpis] = useState<WorkflowKpis | null>(null);
  const [ownCatalog, setOwnCatalog] = useState<WorkflowCatalogEntry[]>([]);
  const [usageByWorkflowId, setUsageByWorkflowId] = useState<Record<string, number>>({});
  const [loadingKpis, setLoadingKpis] = useState(true);
  const [ownLoadingCatalog, setOwnLoadingCatalog] = useState(true);
  const [ownCatalogError, setOwnCatalogError] = useState<unknown>(null);
  // App's ONE copy when it passes it (2026-10-06); this view then reads none.
  const hostsCatalog = catalogFromHost !== undefined;
  const catalog = catalogFromHost ? catalogFromHost.entries : ownCatalog;
  const loadingCatalog = catalogFromHost ? catalogFromHost.loading : ownLoadingCatalog;
  const catalogError = catalogFromHost ? catalogFromHost.error : ownCatalogError;
  const [taglineDone, setTaglineDone] = useState(false);
  const [suggestedRef, setSuggestedRef] = useState<string | null>(null);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [noSuggestionToast, setNoSuggestionToast] = useState(false);
  const [catalogOpen, setCatalogOpen] = useState(!autoSuggestedWorkflowRef);
  const [moreOpen, setMoreOpen] = useState(false);
  const suggestTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    onCountChange?.(activeWorkflows.length);
  }, [activeWorkflows.length, onCountChange]);

  // Hidden descriptors, plus the ora-only workflows the pane never starts
  // (pluginCatalogExclusions). Filtered HERE, not at fetch, so every other
  // catalog consumer keeps the full list.
  const visibleCatalog = useMemo(
    () => catalog.filter((e) => !e.hidden && isLaunchableInPlugin(e)),
    [catalog],
  );

  // Stable-sort:
  //   1. Bucket entries whose user-facing label starts with "[Test]" to the
  //      BOTTOM, alphabetized within the bucket.
  //   2. Non-test entries are sorted by the current user's recent usage
  //      (their automated runs in the last 7 days, per the KPI read's completed_by_workflow), descending.
  //      Source order from the catalog API is the stable tiebreaker — workflows
  //      with zero recent runs preserve their API order.
  //   3. Auto-suggested entry (if any) floats to position 0 regardless of
  //      which bucket it came from — the recommended workflow always wins
  //      the top slot. In practice analyze_email never recommends a [Test]
  //      workflow so this edge case is moot today, but we keep it correct.
  // The user-facing label is the same `entry.name || workflowLabel(entry.ref)`
  // string the CatalogCard renders, so the alphabetic ordering matches what
  // the AM sees.
  const sortedCatalog = useMemo(() => {
    const labelOf = (e: typeof visibleCatalog[number]): string =>
      e.name || workflowLabel(e.ref);
    const isTest = (e: typeof visibleCatalog[number]): boolean =>
      labelOf(e).startsWith("[Test]");

    const nonTest = visibleCatalog
      .map((e, i) => ({ e, i }))
      .filter(({ e }) => !isTest(e))
      .sort((a, b) => {
        const uaCount = usageByWorkflowId[a.e.id] ?? 0;
        const ubCount = usageByWorkflowId[b.e.id] ?? 0;
        if (uaCount !== ubCount) return ubCount - uaCount;
        return a.i - b.i;
      })
      .map(({ e }) => e);
    const tests = visibleCatalog
      .filter(isTest)
      .slice()
      .sort((a, b) => labelOf(a).localeCompare(labelOf(b)));

    const ordered = [...nonTest, ...tests];

    const sortKey = autoSuggestedWorkflowRef ?? null;
    if (!sortKey) return ordered;
    const idx = ordered.findIndex(
      (e) => e.ref === sortKey || e.id === sortKey,
    );
    if (idx <= 0) return ordered;
    const copy = [...ordered];
    const [entry] = copy.splice(idx, 1);
    return [entry, ...copy];
  }, [visibleCatalog, autoSuggestedWorkflowRef, usageByWorkflowId]);

  const metrics = useMemo(() => {
    if (!kpis || kpis.completed_count === 0) return null;
    return { total: kpis.completed_count, hours_saved: kpis.hours_saved };
  }, [kpis]);

  useEffect(() => {
    if (!loadingKpis) onMetricsUpdate?.(metrics);
  }, [metrics, loadingKpis, onMetricsUpdate]);

  const loadKpis = useCallback(async () => {
    try {
      const result = await orchestratorApi.getMyKpis("7d");
      setKpis(result);
      // The catalog's "most used first" sort, from the SAME 7-day automated count
      // the tiles show, grouped per workflow server-side (2026-10-06). It replaced a
      // 200-row completed-instances download this tab counted itself — which also
      // undercounted anyone with more than 200 runs. An API that predates the field
      // sends none: source order.
      setUsageByWorkflowId(result?.completed_by_workflow ?? {});
    } catch {
      // KPI fallback — silent failure leaves the empty-state rendering.
    } finally {
      setLoadingKpis(false);
    }
  }, []);

  const loadCatalog = useCallback(async () => {
    setOwnLoadingCatalog(true);
    setOwnCatalogError(null);
    try {
      const entries = await orchestratorApi.getCatalog();
      setOwnCatalog(Array.isArray(entries) ? entries : []);
    } catch (error) {
      setOwnCatalog([]);
      setOwnCatalogError(error);
    } finally {
      setOwnLoadingCatalog(false);
    }
  }, []);
  const retryCatalog = useCallback(() => {
    if (catalogFromHost) catalogFromHost.onRetry();
    else void loadCatalog();
  }, [catalogFromHost, loadCatalog]);

  useEffect(() => {
    void loadKpis();
    // A host that hands the catalog over owns the read (2026-10-06).
    if (!hostsCatalog) void loadCatalog();
  }, [loadKpis, loadCatalog, hostsCatalog]);
  // Active-list polling is now owned by useActiveInstances above.

  // Tagline animation reveal — match v1's reveal sequencing.
  useEffect(() => {
    const t = setTimeout(() => setTaglineDone(true), 350);
    return () => clearTimeout(t);
  }, []);

  // ── Auto-highlight from App.tsx's pre-fetched recommendation ────────────
  useEffect(() => {
    if (!autoSuggestedWorkflowRef) {
      setCatalogOpen(true);
      return;
    }
    setSuggestedRef(autoSuggestedWorkflowRef);
    setCatalogOpen(false);
    if (suggestTimerRef.current) clearTimeout(suggestTimerRef.current);
    suggestTimerRef.current = setTimeout(
      () => setSuggestedRef(null),
      SUGGEST_RING_TTL_MS,
    );
  }, [autoSuggestedWorkflowRef]);

  // ── "Suggest workflow" — fires analyze_email and highlights match ──
  // Per AC-2: routes through the synchronous /api/workflows/analyze_email
  // endpoint (substrate's analyze_email MCP tool), NOT the legacy two-phase
  // start_workflow path.
  const handleSuggest = useCallback(async () => {
    if (!emailContext) return;
    // Story 26727: an analyze fired during the shared-mailbox detection
    // window would key the triage row on the AM's own address before
    // `resolveTriageMailbox` can see the detected shared target — the same
    // gate the catalog-open launch path already applies (line ~126).
    if (sharedPending) return;
    setSuggestLoading(true);
    setSuggestedRef(null);
    const noClientLoaded = clientCtx?.state !== "done";
    try {
      // The substrate reads the email's PDF attachments from the mailbox via
      // Graph (email_rest_id + mailbox_address, threaded by the builder) — the
      // add-in no longer fetches attachment bytes client-side.
      const result = await orchestratorApi.analyzeEmailSync(
        buildAnalyzeEmailPayload(emailContext),
      );

      // Hydrate client context only when no client was already loaded —
      // a manually-selected client takes precedence.
      if (noClientLoaded && result.client_context?.client) {
        clientCtx?.hydrateFromContext(result.client_context);
      } else if (noClientLoaded && result.extraction.client_name) {
        // beginLookupByName immediately hides the search box (state → "searching")
        // then auto-selects when the result is unambiguous.
        await clientCtx?.beginLookupByName(result.extraction.client_name);
      }

      if (result.recommended_workflow_id) {
        // Propagate to App.tsx so the "Suggested workflow" section and ring
        // highlight both work regardless of whether a client was pre-loaded.
        onSuggestion?.(result.recommended_workflow_id);
      } else {
        setNoSuggestionToast(true);
      }
    } catch {
      setNoSuggestionToast(true);
    } finally {
      setSuggestLoading(false);
    }
  }, [emailContext, clientCtx, sharedPending]);

  useEffect(
    () => () => {
      if (suggestTimerRef.current) clearTimeout(suggestTimerRef.current);
    },
    [],
  );

  const handleClientReset = useCallback(() => {
    clientCtx?.reset();
    setSuggestedRef(null);
    onClearSuggestion?.();
  }, [clientCtx, onClearSuggestion]);

  const sortedActive = useMemo(
    () =>
      activeWorkflows
        .filter((wf) => !wf.hidden)
        .sort((a, b) =>
          (a.name ?? "Unassigned").localeCompare(b.name ?? "Unassigned"),
        ),
    [activeWorkflows],
  );

  // isClientDone is declared once near the top (used by the launch gate).

  const searchClients = useCallback(
    (query: string) => orchestratorApi.searchClientsLegacy(query),
    [],
  );

  const clientTileProps: ClientContextTileProps | null = !demoMode && clientCtx
    ? clientCtx.state === "done" && clientCtx.clientContext
      ? { kind: "loaded", details: clientCtx.clientContext as ClientDetails, onReset: handleClientReset }
      : clientCtx.state === "searching" || clientCtx.state === "analyzing"
        || clientCtx.state === "matched" || clientCtx.state === "gathering"
      ? { kind: "loading" }
      : {
          kind: "not_found",
          suggestedResults: clientCtx.suggestedResults,
          searchResults: clientCtx.searchResults,
          searchLoading: clientCtx.searchLoading,
          onSearch: clientCtx.searchByName,
          onSelectClient: clientCtx.selectClient,
          onSelectClientResult: clientCtx.selectClientFromResult,
          onReset: handleClientReset,
          noEmailSelected: !emailContext,
          searchClients,
        }
    : null;

  return (
    <div className="flex flex-col gap-0 p-4">
      {/* ── Demo banner ─────────────────────────────── */}
      {demoMode && (
        <div className="mb-3 rounded-md border border-primary-warning-300 bg-primary-warning-50 px-3 py-[6px] text-xs font-medium text-primary-warning-700">
          Demo mode — showing sample data.
        </div>
      )}

      {/* ── Tagline + client tile: sticky when client is loaded ─────── */}
      {isClientDone ? (
        <div className="sticky top-0 z-10 -mx-4 bg-primary-gray-light-50 px-4 pt-3 pb-2">
          <div className="mb-2 text-lg font-bold leading-snug text-primary-gray-light-900">
            Automate your workflow.
          </div>
          {clientTileProps && <ClientContextTile {...clientTileProps} />}
        </div>
      ) : (
        <>
          <div className="mb-2 animate-fade-up text-lg font-bold leading-snug text-primary-gray-light-900">
            Automate your workflow.
          </div>
          {clientTileProps && <ClientContextTile {...clientTileProps} />}
        </>
      )}

      {/* ── Suggest workflow button — hidden once a workflow has been suggested ── */}
      {emailContext && !autoSuggestedWorkflowRef && (
        <div className="mb-3">
          <button
            type="button"
            onClick={handleSuggest}
            disabled={suggestLoading || sharedPending}
            data-testid="suggest-workflow-button"
            className={
              "w-full rounded-md border border-primary-gray-light-300 bg-base-white px-3 py-[6px] text-xs font-medium transition-colors " +
              (suggestLoading || sharedPending
                ? "cursor-wait text-primary-gray-light-400"
                : "text-primary-gray-light-700 hover:border-sunrise hover:text-primary-gray-light-900")
            }
          >
            {suggestLoading ? (
              <span className="flex items-center justify-center gap-1.5">
                <Spinner size={14} />
                Analyzing email…
              </span>
            ) : (
              "Suggest workflow"
            )}
          </button>
        </div>
      )}

      {/* ── "Launch <workflow_title>" CTA ──────────────────────────────────
           Rendered whenever analyze_email returned a recommended_workflow_id
           that resolves to a catalog entry. The recommendation is the
           addin's primary CTA after parsing an email — there's no scenario
           where surfacing it as a one-click launch is wrong.
      */}
      {false && autoSuggestedWorkflowRef && !loadingCatalog && (() => {
        const recommendedEntry = catalog.find(
          (e) => e.ref === autoSuggestedWorkflowRef || e.id === autoSuggestedWorkflowRef,
        );
        if (!recommendedEntry) return null;
        const entry = recommendedEntry as WorkflowCatalogEntry;
        const title = entry.name ?? workflowLabel(autoSuggestedWorkflowRef as string);
        return (
          <div className="mb-3">
            <button
              type="button"
              data-testid="launch-recommended-cta"
              onClick={() => {
                const schemaProps =
                  entry.input_schema &&
                  typeof entry.input_schema.properties === "object" &&
                  entry.input_schema.properties !== null
                    ? Object.keys(
                        entry.input_schema.properties as Record<string, unknown>,
                      )
                    : [];
                const emailFields: Record<string, unknown> = {};
                if (emailContext) {
                  const candidates: Record<string, unknown> = {
                    email_subject: emailContext.subject,
                    email_body: emailContext.body,
                    sender: emailContext.sender,
                    client_id: clientCtx?.clientContext?.client?.client_sk ?? null,
                  };
                  for (const key of schemaProps) {
                    if (Object.prototype.hasOwnProperty.call(candidates, key)) {
                      emailFields[key] = candidates[key];
                    }
                  }
                }
                onLaunchRecommended?.(entry, emailFields);
              }}
              className="w-full rounded-md border border-sunrise bg-sunrise px-3 py-2 text-xs font-semibold text-primary-gray-light-900 transition-colors hover:bg-blossom"
            >
              Launch {title}
            </button>
          </div>
        );
      })()}

      {/* ── KPI tiles or empty state ─────────────────── */}
      {taglineDone && metrics && metrics.total > 0 ? (
        <div className="mb-1 flex gap-[10px]">
          {[
            {
              key: "total",
              value: metrics.total.toLocaleString(),
              suffix: null,
              label: "Automated",
              tooltip: "Workflows completed in the last 7 days",
            },
            {
              key: "hours",
              value: metrics.hours_saved.toLocaleString(),
              suffix: "hrs",
              label: "Saved",
              tooltip: "Hours saved by automated workflows in the last 7 days",
            },
          ].map(({ key, value, suffix, label, tooltip }) => (
            <div
              key={key}
              title={tooltip}
              data-testid={`kpi-tile-${key}`}
              className="flex-1 animate-fade-up cursor-default rounded-[10px] border border-primary-gray-light-200 bg-base-white p-4 text-center"
            >
              <div className="text-display-xs font-bold leading-none text-primary-gray-light-900">
                {value}
                {suffix && (
                  <span className="ml-[3px] text-md font-semibold text-primary-gray-light-500">
                    {suffix}
                  </span>
                )}
              </div>
              <div className="mt-[6px] text-xs font-medium text-primary-gray-light-500">
                {label}
              </div>
            </div>
          ))}
        </div>
      ) : taglineDone && (loadingKpis || suggestLoading) ? (
        <div className="mb-1 flex gap-[10px]" data-testid="kpi-loading">
          <Skeleton className="h-[68px] flex-1" />
          <Skeleton className="h-[68px] flex-1" />
        </div>
      ) : taglineDone && metrics === null ? (
        <div
          className="mb-1 rounded-[10px] border border-dashed border-primary-gray-light-200 bg-primary-gray-light-25 p-3 text-center text-xs text-primary-gray-light-500"
          data-testid="kpi-empty"
        >
          Your KPIs will appear here once you complete a workflow.
        </div>
      ) : null}
      {taglineDone && (
        <div className="mb-4 text-center text-2xs text-primary-gray-light-400">
          Showing the last 7 days.
        </div>
      )}

      {/* ── In-flight section ─────────────────────────── */}
      {taglineDone && !loadingActive && sortedActive.length > 0 && (
        <section className="mb-5">
          <div className="mb-[10px]">
            <div className="mb-[4px] flex items-center gap-[6px]">
              <span className="text-xs font-bold uppercase tracking-[0.06em] text-primary-gray-light-400">
                In flight
              </span>
              {!demoMode && (
                <div
                  className={
                    "h-[6px] w-[6px] rounded-full " +
                    (isLiveConnected
                      ? "bg-primary-success-600 motion-safe:animate-live-pulse"
                      : "bg-primary-gray-light-300")
                  }
                />
              )}
            </div>
            <p className="text-xs text-primary-gray-light-500">
              We read the exclusions so you don't have to.
            </p>
          </div>
          <div
            className="flex flex-col gap-2 overflow-y-auto overscroll-contain"
            style={{ maxHeight: "170px" }}
            data-testid="inflight-scroll-container"
          >
            {sortedActive.map((wf) => {
              const catalogEntry = catalog.find((e) => e.id === wf.workflow_id);
              return (
                <ActiveWorkflowCard
                  key={wf.instance_id}
                  wf={wf}
                  catalogEntry={catalogEntry}
                  isActive={wf.instance_id === activeInstanceId}
                  onClick={() => onOpenWorkflow(wf.instance_id, wf.workflow_id, catalogEntry)}
                />
              );
            })}
          </div>
        </section>
      )}

      {/* ── Launch a workflow ─────────────────────────── */}
      {taglineDone && (() => {
        const isSuccess = isClientDone;

        const catalogContent = loadingCatalog ? (
          <div className="flex flex-col gap-2" data-testid="catalog-loading">
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton
                key={i}
                className="h-[68px] rounded-[10px] border border-primary-warning-300"
              />
            ))}
          </div>
        ) : catalogError ? (
          <ErrorPanel
            error={catalogError}
            title="Couldn't load workflows"
            description="We couldn't fetch the workflow catalog. Please try again."
            onRetry={retryCatalog}
            compact
          />
        ) : sortedCatalog.length === 0 ? (
          <div
            className="rounded-[10px] border border-dashed border-primary-gray-light-200 bg-primary-gray-light-25 p-4 text-center text-xs text-primary-gray-light-500"
            data-testid="catalog-empty"
          >
            No workflows available for your role. Contact your manager.
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {sortedCatalog.map((entry) => (
              <CatalogCard
                key={entry.ref}
                entry={entry}
                onClick={() => gatedOpenCatalog(entry)}
                disabled={workflowRequiresClient(entry) && !isClientDone}
                disabledReason="Select a client first"
                highlighted={
                  suggestedRef === entry.ref || suggestedRef === entry.id
                }
              />
            ))}
          </div>
        );

        // Use the stable prop for placement (persists through session); suggestedRef is TTL-only for the ring highlight.
        // Not gated on isSuccess so the split view appears as soon as the suggestion arrives,
        // without waiting for the client lookup to complete.
        const suggestedEntry =
          autoSuggestedWorkflowRef && !loadingCatalog
            ? sortedCatalog.find(
                (e) =>
                  e.ref === autoSuggestedWorkflowRef ||
                  e.id === autoSuggestedWorkflowRef,
              )
            : undefined;
        const remainingCatalog = suggestedEntry
          ? sortedCatalog.filter((e) => e !== suggestedEntry)
          : sortedCatalog;

        const remainingContent = loadingCatalog ? null : remainingCatalog.length === 0 ? null : (
          <div className="flex flex-col gap-2">
            {remainingCatalog.map((entry) => (
              <CatalogCard
                key={entry.ref}
                entry={entry}
                onClick={() => gatedOpenCatalog(entry)}
                disabled={workflowRequiresClient(entry) && !isClientDone}
                disabledReason="Select a client first"
                highlighted={false}
              />
            ))}
          </div>
        );

        return (
          <section>
            {(isSuccess || !!autoSuggestedWorkflowRef) ? (
              <>
                {suggestedEntry && (
                  <>
                    <div className="mb-[10px] text-[11px] font-bold uppercase tracking-[0.06em] text-primary-gray-light-400">
                      Suggested workflow
                    </div>
                    <div className="mb-3">
                      <CatalogCard
                        entry={suggestedEntry}
                        onClick={() => gatedOpenCatalog(suggestedEntry)}
                        disabled={workflowRequiresClient(suggestedEntry) && !isClientDone}
                        disabledReason="Select a client first"
                        highlighted
                      />
                    </div>
                  </>
                )}
                {remainingContent && (
                  <div data-testid="catalog-accordion">
                    <button
                      type="button"
                      onClick={() => setMoreOpen((o) => !o)}
                      className="mb-[10px] flex w-full items-center gap-[4px] text-left text-[11px] font-bold uppercase tracking-[0.06em] text-primary-gray-light-400 hover:text-primary-gray-light-600"
                    >
                      <span
                        className="inline-block transition-transform duration-200"
                        style={{ transform: moreOpen ? "rotate(90deg)" : "rotate(0deg)" }}
                      >
                        ›
                      </span>
                      More workflows
                    </button>
                    {moreOpen && remainingContent}
                  </div>
                )}
                {!suggestedEntry && !remainingContent && catalogContent}
              </>
            ) : (
              <>
                <div className="mb-[10px] animate-fade-up text-[11px] font-bold uppercase tracking-[0.06em] text-primary-gray-light-400">
                  Launch a workflow
                </div>
                {catalogContent}
              </>
            )}
          </section>
        );
      })()}

      <Toast
        open={noSuggestionToast}
        message="No workflow could be suggested."
        severity="info"
        onClose={() => setNoSuggestionToast(false)}
      />
    </div>
  );
};

export default LaunchTab;
